import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { hostLogin, hostLogout, hostMe } from '../api/hostApi';
import { adminLogin, adminLogout, adminMe } from '../api/adminApi';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [kind, setKind] = useState(null); // 'host' | 'admin'
  const [loading, setLoading] = useState(true);

  // Try to restore session: check /host/me and /admin/me (they use the same session/cookie realm)
  const restore = useCallback(async () => {
    setLoading(true);
    try {
      const h = await hostMe().catch(() => null);
      if (h) { setUser(h.host || h); setKind('host'); return; }
      const a = await adminMe().catch(() => null);
      if (a) { setUser(a.admin || a); setKind('admin'); return; }
      setUser(null); setKind(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { restore(); }, [restore]);

  const login = async (which, { email, password, remember = true }) => {
    const fn = which === 'admin' ? adminLogin : hostLogin;
    const data = await fn(email, password, remember);
    const profile = data?.host || data?.admin || data;
    setUser(profile); setKind(which);
    return profile;
  };

  const logout = async () => {
    try { kind === 'admin' ? await adminLogout() : await hostLogout(); } catch (_) {}
    setUser(null); setKind(null);
  };

  return (
    <AuthContext.Provider value={{ user, kind, loading, login, logout, refresh: restore }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
